import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

export default function FreeGuide({ guide }) {
  if (!guide) return null;

  const { start, end, numberOfStops, recommendedStopCities, totalDrivingDays, totalDrivingTime, highlights,
    totalDistanceKm, estimatedFuelCostEUR, estimatedTollCostEUR, estimatedTotalCostEUR, tollBreakdown } = guide;

  const [tollOpen, setTollOpen] = useState(false);
  const navigate = useNavigate();

  const formatDriveTime = (str) => {
    if (!str) return "";
    if (/^\d+\.?\d*h/.test(str)) return str;
    const hoursMatch = str.match(/(\d+)\s*hours?/i);
    const minutesMatch = str.match(/(\d+)\s*minutes?/i);
    const hours = parseInt(hoursMatch?.[1] || 0);
    const minutes = parseInt(minutesMatch?.[1] || 0);
    const total = hours + minutes / 60;
    return total.toFixed(1) + "h";
  };

  const displayDistance = totalDistanceKm > 0 ? totalDistanceKm.toLocaleString() : "—";

  // Build the route chain: Start → Stop1 → Stop2 → … → End
  const routeChain = [start, ...(recommendedStopCities || []), end];

  return (
    <div className="w-full max-w-md bg-white p-6 rounded-2xl shadow-lg">
      <h2 className="text-2xl font-bold text-primary mb-1">Your Free Route Preview</h2>
      <p className="text-sm text-gray-500 mb-5">
        {start} → {end}
      </p>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-background rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-primary">{totalDrivingDays}</p>
          <p className="text-xs text-gray-500 mt-1">driving days</p>
        </div>
        <div className="bg-background rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-primary">{numberOfStops}</p>
          <p className="text-xs text-gray-500 mt-1">overnight stops</p>
        </div>
        <div className="bg-background rounded-xl p-3 text-center">
          <p className="text-lg font-bold text-primary leading-tight">
            {formatDriveTime(totalDrivingTime)}
          </p>
          <p className="text-xs text-gray-500 mt-1">approx. driving time</p>
        </div>
      </div>

      {/* Route chain */}
      <div className="mb-6">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Route</h3>
        <div className="flex flex-wrap items-center gap-1">
          {routeChain.map((city, i) => (
            <React.Fragment key={i}>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  i === 0 || i === routeChain.length - 1
                    ? "bg-primary text-white"
                    : "bg-accent/20 text-primary border border-accent"
                }`}
              >
                {city}
              </span>
              {i < routeChain.length - 1 && (
                <span className="text-gray-400 text-sm">→</span>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Highlights */}
      {highlights && highlights.length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Highlights</h3>
          <ul className="space-y-2">
            {highlights.map((point, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                <span className="mt-0.5 text-highlight font-bold">✓</span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Cost Estimate */}
      {estimatedTotalCostEUR != null && (
        <div className="mb-6">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Trip Cost Estimate</h3>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="bg-background rounded-xl p-3 text-center">
              <p className="text-lg font-bold text-primary leading-tight">{displayDistance} km</p>
              <p className="text-xs text-gray-500 mt-1">total distance</p>
            </div>
            <div className="bg-background rounded-xl p-3 text-center">
              <p className="text-lg font-bold text-primary leading-tight">€{estimatedFuelCostEUR?.toFixed(2)}</p>
              <p className="text-xs text-gray-500 mt-1">fuel cost</p>
            </div>
            <div className="bg-background rounded-xl p-3 text-center">
              <p className="text-lg font-bold text-primary leading-tight">€{estimatedTollCostEUR?.toFixed(2)}</p>
              <p className="text-xs text-gray-500 mt-1">tolls</p>
            </div>
          </div>

          {/* Toll breakdown expandable */}
          {tollBreakdown && tollBreakdown.length > 0 && (
            <div className="mb-3">
              <button
                onClick={() => setTollOpen((o) => !o)}
                className="w-full flex items-center justify-between px-4 py-2 bg-background rounded-xl text-sm font-medium text-primary hover:bg-accent/10 transition"
              >
                <span>Toll breakdown by country</span>
                <span className="text-gray-400">{tollOpen ? "▲" : "▼"}</span>
              </button>
              {tollOpen && (
                <div className="mt-1 border border-gray-100 rounded-xl overflow-hidden">
                  {tollBreakdown.map((item, i) => (
                    <div
                      key={i}
                      className={`flex items-start justify-between gap-3 px-4 py-3 text-sm ${
                        i % 2 === 0 ? "bg-white" : "bg-background"
                      }`}
                    >
                      <div>
                        <p className="font-medium text-primary">{item.country}</p>
                        <p className="text-xs text-gray-500">{item.description}</p>
                      </div>
                      <p className="font-semibold text-primary whitespace-nowrap">
                        {item.estimatedCostEUR === 0 ? "—" : `€${item.estimatedCostEUR.toFixed(2)}`}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="bg-accent/10 border border-accent rounded-xl p-4 text-center">
            <p className="text-xs text-gray-500 mb-1">Total Estimated Cost</p>
            <p className="text-3xl font-bold text-primary">€{estimatedTotalCostEUR?.toFixed(2)}</p>
          </div>
          <p className="text-xs text-gray-400 mt-2 text-center">Cost estimates are approximate. Toll rates and fuel prices vary — always verify before travel.</p>
        </div>
      )}

      {/* CTAs */}
      <div className="mt-2 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <Link
            to="/upgrade"
            state={{ fromApp: true }}
            className="text-center px-3 py-3 rounded-xl shadow-md bg-primary text-white font-semibold hover:bg-accent transition text-sm"
          >
            Upgrade to Full Guide
          </Link>
          <button
            onClick={() => {
              document.getElementById('full-guide-info')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="text-center px-3 py-3 rounded-xl shadow-md bg-highlight text-white font-semibold hover:bg-primary transition text-sm"
          >
            What is the Full Guide? ↓
          </button>
        </div>
        <button
          onClick={() => { localStorage.removeItem("pendingTrip"); navigate("/"); }}
          className="block w-full text-center px-6 py-3 rounded-xl shadow-md bg-white border hover:bg-background transition text-sm"
        >
          Plan another trip
        </button>
      </div>
    </div>
  );
}
